import { Fragment, useMemo } from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  ActionButton,
  Button,
  DropZone,
  FileTrigger,
  Link,
  LabeledValue,
  LinkButton,
  Text,
  mergeStyles,
} from "@react-spectrum/s2";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import MagicWandIcon from "@react-spectrum/s2/icons/MagicWand";
import AIGenerateIcon from "icon:@app/assets/AIGenerate.svg";

import { store } from "@app/store";
import {
  IMAGE_DROP_ZONE_STYLE,
  getImageDragOperation,
} from "@app/components/ImageAsset";
import {
  ThumbnailCaption,
  resolveCaptionText,
} from "@app/components/ThumbnailCaption";
import {
  useImageVariation,
  resolveReferenceAssets,
} from "@app/hooks/imageVariations";
import { useIsDevMode } from "@app/hooks/useRoles";
import {
  GRID_TYPE,
  getCellLink,
  getGroupGrids,
  getPreviewLinks,
  isCellDropDisabled,
  isTextCell,
  getVariationReferenceKeys,
} from "./wizardSteps";
import {
  OUTDATED_ACTION,
  getStructureDependencies,
} from "./variationDependencies";
import { getVariationOutdatedState } from "./variationOutdatedState";
import { useOutdatedUpdate } from "./useOutdatedUpdate";
import {
  CampaignField,
  CampaignFieldRows,
  CampaignGridHeader,
  CampaignGroupHeader,
} from "./CampaignPreview";
import { ImageVariationCard, ReferenceImages } from "./ImageVariations";
import { STAGES, VARIATION_PROGRESS_STAGE } from "./VariationProgress";
import {
  TemplateColorAsset,
  TemplateImageAsset,
  getExtendedTemplateAssets,
} from "./TemplateAsset";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

const PREVIEW_WIDTH = 480;

// Every row of a variations grid has this many columns, so its cards all share
// one size however many a row holds.
const VARIATIONS_GRID_COLUMNS = 5;

/** A preview card and its caption, `isFluid` filling its container's width. */
const PreviewFrame = ({ isFluid = false, children }) => (
  <div
    className={style({
      display: "flex",
      flexDirection: "column",
      width: { default: PREVIEW_WIDTH, isFluid: "full" },
      maxWidth: "full",
      flexShrink: 0,
    })({ isFluid })}
  >
    {children}
  </div>
);

PreviewFrame.propTypes = {
  isFluid: PropTypes.bool,
  children: PropTypes.node,
};

const FIELD_BOX_STYLE = style({
  // With `isOffset`, lines the box up with the previews beside it, which a
  // `LabeledValue` label precedes: the label's line plus the padding below it
  // (half of what is left of the field's 32px height), as the label sets them.
  font: "ui",
  marginTop: {
    default: 0,
    isOffset: `[calc((${size(32)} + 1lh) / 2)]`,
  },
  paddingX: 24,
  paddingY: 12,
  borderRadius: "lg",
  borderColor: "gray-100",
  borderWidth: 1,
  borderStyle: "solid",
  backgroundColor: "layer-1",
});

// Fills the subgrid cell it is in, passing its tracks on to its content.
const FIELD_BOX_SUBGRID_STYLE = style({
  // With `isOffset`, lines the box up with the previews beside it, which a
  // `LabeledValue` label precedes: the label's line plus the padding below it
  // (half of what is left of the field's 32px height), as the label sets them.
  font: "ui",
  marginTop: {
    default: 0,
    isOffset: `[calc((${size(32)} + 1lh) / 2)]`,
  },
  paddingX: 24,
  paddingY: 12,
  borderRadius: "lg",
  borderColor: "gray-100",
  borderWidth: 1,
  borderStyle: "solid",
  backgroundColor: "layer-1",
  display: "grid",
  gridTemplateColumns: { default: "minmax(0, 1fr)", md: "subgrid" },
  gridColumnStart: "1",
  gridColumnEnd: "-1",
});

/**
 * The box campaign fields are shown in — a whole `fields` grid, or a single
 * text cell of a `variations` grid. `isSubgrid` makes it a subgrid of the
 * grid cell it fills, for fields laid out on that grid's tracks. `isOffset`
 * moves it below the label of the previews beside it.
 */
const CampaignFieldBox = ({
  isSubgrid = false,
  isOffset = false,
  children,
}) => (
  <div
    className={(isSubgrid ? FIELD_BOX_SUBGRID_STYLE : FIELD_BOX_STYLE)({
      isOffset,
    })}
  >
    {children}
  </div>
);

CampaignFieldBox.propTypes = {
  isSubgrid: PropTypes.bool,
  isOffset: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * An image variation of the campaign structure — linked to a field, or a cell
 * of a variations grid: its preview, flagged like a failed generation when a
 * campaign value or reference image it was generated from has changed since (see
 * `variationDependencies`; `dependencies` is what the structure adds to them).
 * References and regeneration live in the `EditVariationDialog` the preview
 * opens, which checks the same live state.
 *
 * `label` heads the card, `description` captions it below — the latter defaulting to
 * the template variation's own, `null` hiding it — and `isFluid` fills the
 * width of its container rather than a fixed preview width.
 *
 * With `onUploadVariation`, an image dropped onto the card becomes the
 * variation's image — unless `isDropDisabled`, when the card is no drop zone
 * and the idle one offers to upload an image instead. A variation in
 * `idleKeys` was never generated: instead of its progress, it offers to
 * generate it (`onGenerateVariation`).
 */
const CampaignFieldVariation = ({
  dependencies,
  label,
  description,
  isFluid = false,
  variation,
  selectedCampaign,
  video,
  templateData,
  generatingKeys,
  idleKeys,
  isLoading,
  isGenerateDisabled = false,
  onEditVariation,
  onGenerateVariation,
  onUploadVariation,
  isDropDisabled = false,
}) => {
  const {
    progress,
    isPromptEdited,
    isBusy,
    regenerate,
    regenerateFromCampaign,
  } = useImageVariation({
    projectId: video.data?.id,
    variationKey: variation.key,
    referenceKey: variation.referenceKey,
    video,
    templateData,
  });

  const assets = video.data?.assets;
  const asset = assets?.find((a) => a.name === variation.key);
  const isGenerating = !!generatingKeys?.has(variation.key) || !!progress;
  const isUploading = !!asset?.isLoading;
  // Never generated, and waiting for the user to generate it.
  const isIdle =
    !!idleKeys?.has(variation.key) && !asset?.filePath && !isGenerating;

  const { isOutdated, action: outdatedAction } = getVariationOutdatedState({
    variation,
    dependencies,
    assets,
    selectedCampaign,
    video,
    isPromptEdited,
    isBusy: isGenerating || isUploading,
  });

  const { update, confirmDialog } = useOutdatedUpdate({
    action: outdatedAction,
    isPromptEdited,
    regenerateFromCampaign,
    onKeepPrompt: () => regenerate(),
  });

  const canDrop = !!onUploadVariation && !isDropDisabled;

  const card = (
    <ImageVariationCard
      variation={variation}
      asset={asset}
      assets={assets}
      isLoading={isLoading || isGenerating || isUploading}
      progress={
        progress ??
        (!asset?.filePath && !variation.data?.visualPrompt
          ? {
              ...STAGES[VARIATION_PROGRESS_STAGE.WAITING],
              label: isUploading ? "Uploading" : undefined,
            }
          : undefined)
      }
      isOutdated={isOutdated}
      isIdle={isIdle && !isUploading}
      isInDropZone={canDrop}
      onEdit={
        isIdle
          ? undefined
          : (editedVariation) =>
              onEditVariation?.({
                ...editedVariation,
                dependencies,
              })
      }
      aspectRatio="4 / 3"
      // Flat inside a drop zone, like `ImageAsset`'s preview.
      styles={
        canDrop
          ? style({ width: "full", boxShadow: "none" })
          : style({ width: "full" })
      }
    >
      {isIdle && !isUploading && !!onGenerateVariation && (
        <div
          className={style({
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            padding: 16,
            textAlign: "center",
          })}
        >
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 4,
            })}
          >
            <Text styles={style({ font: "title" })}>
              Generate {(variation.label ?? variation.key).toLowerCase()}
            </Text>
            {canDrop && (
              <Text
                styles={style({ font: "detail-sm", color: "neutral-subdued" })}
              >
                or drag and drop an image
              </Text>
            )}
            {!!onUploadVariation && isDropDisabled && (
              <Text
                styles={style({ font: "detail-sm", color: "neutral-subdued" })}
              >
                or
                <FileTrigger
                  acceptedFileTypes="image/*"
                  onSelect={(files) => onUploadVariation(files, variation.key)}
                >
                  <Link
                    variant="secondary"
                    isDisabled={isGenerateDisabled}
                    styles={style({ marginStart: 4 })}
                  >
                    select a file
                  </Link>
                </FileTrigger>
              </Text>
            )}
          </div>
          <Button
            variant="primary"
            size="M"
            isDisabled={isGenerateDisabled}
            onPress={() => onGenerateVariation(variation)}
          >
            <MagicWandIcon />
            <Text>Generate</Text>
          </Button>
        </div>
      )}
    </ImageVariationCard>
  );

  const content = canDrop ? (
    // The same drop zone as `ImageAsset`'s, dashed border included.
    <DropZone
      aria-label={`Drop an image for ${label ?? variation.label ?? variation.key}`}
      isFilled={!!asset?.filePath}
      // A generation still running would overwrite the dropped image.
      getDropOperation={(types) =>
        isGenerating || isUploading || isLoading
          ? "cancel"
          : getImageDragOperation(types)
      }
      onDrop={(event) => onUploadVariation(event.items, variation.key)}
      styles={style({ width: "full" })}
      UNSAFE_style={IMAGE_DROP_ZONE_STYLE}
    >
      <div className={style({ width: "full" })}>{card}</div>
    </DropZone>
  ) : (
    card
  );

  return (
    <PreviewFrame isFluid={isFluid}>
      {/* Only a heading: a `LabeledValue` cannot contain the file input. */}
      {!!label && <LabeledValue label={label} value="" />}
      {content}

      <ThumbnailCaption
        description={resolveCaptionText(description, variation.description)}
        actions={
          !!outdatedAction && (
            <ActionButton
              size="XS"
              isDisabled={isBusy || isLoading}
              onPress={update}
            >
              <AIGenerateIcon />
              <Text>Update</Text>
            </ActionButton>
          )
        }
      />

      {confirmDialog}
    </PreviewFrame>
  );
};

CampaignFieldVariation.propTypes = {
  dependencies: PropTypes.shape({
    keys: PropTypes.arrayOf(PropTypes.string),
    labels: PropTypes.instanceOf(Map),
  }),
  label: PropTypes.string,
  description: PropTypes.string,
  isFluid: PropTypes.bool,
  variation: PropTypes.object.isRequired,
  selectedCampaign: PropTypes.object,
  video: PropTypes.object.isRequired,
  templateData: PropTypes.object,
  generatingKeys: PropTypes.instanceOf(Set),
  idleKeys: PropTypes.instanceOf(Set),
  isLoading: PropTypes.bool,
  isGenerateDisabled: PropTypes.bool,
  onEditVariation: PropTypes.func,
  onGenerateVariation: PropTypes.func,
  onUploadVariation: PropTypes.func,
  isDropDisabled: PropTypes.bool,
};

/**
 * Catches every outdated variation of a grid up at once (`updates`, each its
 * `key`, `action` and whether its prompt `isPromptEdited`) through `onUpdate`
 * — asking first before overwriting prompts the user wrote.
 */
const UpdateOutdatedButton = ({ updates, isDisabled = false, onUpdate }) => {
  const edited = updates.filter(
    (u) => u.action === OUTDATED_ACTION.PROMPT_AND_IMAGE && u.isPromptEdited,
  );

  const { update, confirmDialog } = useOutdatedUpdate({
    action: OUTDATED_ACTION.PROMPT_AND_IMAGE,
    isPromptEdited: edited.length > 0,
    editedCount: edited.length,
    // Each as its own action asks.
    regenerateFromCampaign: () => onUpdate(updates),
    // The edited prompts kept: only their images are generated again.
    onKeepPrompt: () =>
      onUpdate(
        updates.map((u) =>
          edited.includes(u) ? { ...u, action: OUTDATED_ACTION.IMAGE } : u,
        ),
      ),
  });

  return (
    <>
      <ActionButton size="XS" isDisabled={isDisabled} onPress={update}>
        <AIGenerateIcon />
        <Text>Update all</Text>
      </ActionButton>
      {confirmDialog}
    </>
  );
};

UpdateOutdatedButton.propTypes = {
  updates: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      action: PropTypes.string.isRequired,
      isPromptEdited: PropTypes.bool,
    }),
  ).isRequired,
  isDisabled: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
};

/**
 * The reference image(s) a campaign field or variations grid cell points at
 * (`referenceKey`), badged as references. A reference that does not exist yet
 * shows its loading state; opening one shows it in the `EditVariationDialog`.
 */
const CampaignFieldReference = ({
  referenceKey,
  label,
  description,
  isFluid = false,
  video,
  templateData,
  imageVariations,
  isLoading,
  onEditVariation,
  onUploadAsset,
}) => {
  const colorScheme = useStore(store, (state) => state.colorScheme);
  const progressByKey = useStore(
    store,
    (state) => state.imageVariationProgress,
  );

  const assets = resolveReferenceAssets(video.data?.assets, { referenceKey });

  return (
    <PreviewFrame isFluid={isFluid}>
      {!!label && <LabeledValue label={label} value="" />}

      <ReferenceImages
        assets={assets}
        urlByPath={imageVariations?.referenceUrlByPath}
        isLoading={isLoading}
        progressByKey={progressByKey}
        skipImageLabeling={imageVariations?.skipImageLabeling}
        isLabeled={imageVariations?.labeling?.isLabeled}
        templateData={templateData}
        colorScheme={colorScheme}
        aspectRatio="4 / 3"
        onUpload={onUploadAsset}
        onEdit={(asset) =>
          onEditVariation?.({
            key: asset?.name,
            referenceKey: asset?.name,
            isReferenceAsset: true,
            type: "original",
            data: { ...asset?.labeling },
          })
        }
      />

      <ThumbnailCaption description={description} />
    </PreviewFrame>
  );
};

CampaignFieldReference.propTypes = {
  referenceKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  isFluid: PropTypes.bool,
  video: PropTypes.object.isRequired,
  templateData: PropTypes.object,
  imageVariations: PropTypes.object,
  isLoading: PropTypes.bool,
  onEditVariation: PropTypes.func,
  onUploadAsset: PropTypes.func,
};

/**
 * The link a campaign field or variations grid cell shows (`link`, see
 * `getCellLink`), as a `Link` or — `linkType: "button"` — a `LinkButton`, or
 * — `linkType: "embed"` — the page itself in a sandboxed frame, with a link to
 * open it on its own for the sites that refuse to be embedded. Captioned like
 * the other items.
 */
const CampaignFieldLink = ({ link, label, description, isFluid = false }) => {
  const Component = link.type === "button" ? LinkButton : Link;
  const isEmbed = link.type === "embed";

  return (
    <PreviewFrame isFluid={isFluid}>
      {!!label && <LabeledValue label={label} value="" />}

      {isEmbed && (
        <iframe
          src={link.href}
          title={link.label}
          sandbox={link.sandbox}
          referrerPolicy="no-referrer"
          loading="lazy"
          className={style({
            display: "block",
            width: "full",
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "gray-100",
            borderRadius: "lg",
            backgroundColor: "layer-1",
          })}
          style={{ aspectRatio: link.aspectRatio }}
        />
      )}
      {!isEmbed && (
        <div className={style({ display: "flex", alignItems: "start" })}>
          <Component
            variant="secondary"
            isStandalone
            href={link.href}
            target={link.target}
            rel={link.rel}
          >
            {link.label}
          </Component>
        </div>
      )}

      <ThumbnailCaption description={description} />
    </PreviewFrame>
  );
};

CampaignFieldLink.propTypes = {
  link: PropTypes.shape({
    href: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    aspectRatio: PropTypes.string,
    sandbox: PropTypes.string,
  }).isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  isFluid: PropTypes.bool,
};

/**
 * The template asset a campaign field or variations grid cell points at
 * (`assetName`), filled right there as in `TemplateAssets` — without its
 * `context`. An image is framed like the variation cards; a color is the usual
 * swatch. Both are captioned like the variation cards by their `label` and
 * `description` (the asset's own by default, `null` hiding them).
 */
const CampaignFieldAsset = ({
  asset,
  selectedAsset,
  label,
  description,
  isFluid = false,
  scope,
  colors,
  onUploadAsset,
  onDeleteAsset,
  onColorChange,
}) =>
  asset.type === "color" ? (
    // The label heads the frame and the caption spans it, as a variation
    // card's does; the swatch keeps its own size.
    <PreviewFrame isFluid={isFluid}>
      <TemplateColorAsset
        asset={asset}
        selectedAsset={selectedAsset}
        label={label}
        description={description}
        scope={scope}
        colors={colors}
        onChange={onColorChange}
        isCaptioned
      />
    </PreviewFrame>
  ) : (
    <PreviewFrame isFluid={isFluid}>
      <TemplateImageAsset
        // The merged asset, as `TemplateAssets` renders its images.
        asset={selectedAsset ?? asset}
        selectedAsset={selectedAsset}
        label={label}
        description={description}
        scope={scope}
        onDrop={onUploadAsset}
        onDelete={onDeleteAsset}
        aspectRatio="4 / 3"
        isFluid
        isCaptioned
      />
    </PreviewFrame>
  );

CampaignFieldAsset.propTypes = {
  asset: PropTypes.object.isRequired,
  selectedAsset: PropTypes.object,
  label: PropTypes.string,
  description: PropTypes.string,
  isFluid: PropTypes.bool,
  scope: PropTypes.object,
  colors: PropTypes.array,
  onUploadAsset: PropTypes.func,
  onDeleteAsset: PropTypes.func,
  onColorChange: PropTypes.func,
};

/**
 * A campaign structure, as `CampaignPreview` renders it, where a field linked
 * to an image variation (`variationKey`) shows that variation's preview next
 * to its text, and one linked to a template asset (`assetName`) lets the user
 * fill that asset right there, as in `TemplateAssets`.
 *
 * Presentational: the variations and their generation state come from the
 * wizard's image variations pipeline (`imageVariations`).
 *
 * `onUploadVariation(items, key)` lets an image be dropped onto a variation's
 * card to become its image. `onGenerateVariation(variation)` puts a Generate
 * button on the cards of a `manual` pipeline's never-generated variations
 * (`imageVariations.idleKeys`), disabled by `isGenerateDisabled`.
 *
 * Template assets are filled through `onUploadAsset(items, name)` (images,
 * with `onDeleteAsset(asset)` for optional ones) and `onColorChange` (colors,
 * picked from `colors`); an asset whose handler is missing is not shown.
 */
export const CampaignVisualsPreview = ({
  campaignStructure = [],
  selectedCampaign = {},
  selectedPersona = {},
  canEditCampaign = true,
  video,
  templateData,
  imageVariations,
  colors,
  isGenerateDisabled = false,
  onEditVariation,
  onGenerateVariation,
  onUploadVariation,
  onUploadAsset,
  onDeleteAsset,
  onColorChange,
  renderVariation,
  renderAsset,
  styles,
}) => {
  const groups = imageVariations?.groups ?? [];
  const isDevMode = useIsDevMode();

  // Which variations are being generated, so they are not outdated meanwhile.
  const progressByKey = useStore(
    store,
    (state) => state.imageVariationProgress,
  );

  // Dev only: regenerates the images of `keys`, even those that have one —
  // asking for the ones a manual pipeline is still waiting on, too.
  const regenerateVariations = (keys) => {
    imageVariations?.generate?.(keys);
    imageVariations?.run?.({ force: true, keys });
  };

  // What the structure adds to its variations' dependencies, by key.
  const structureDependencies = useMemo(
    () => getStructureDependencies(campaignStructure),
    [campaignStructure],
  );

  const extendedAssets = useMemo(
    () => getExtendedTemplateAssets(templateData?.assets, video.data?.assets),
    [templateData?.assets, video.data?.assets],
  );

  // What the assets' use-case `condition`s are evaluated with.
  const scope = useMemo(
    () => ({ video, template: { data: templateData }, selectedCampaign }),
    [video, templateData, selectedCampaign],
  );

  const findAsset = (name) => {
    const asset = name
      ? templateData?.assets?.find((a) => a.name === name)
      : null;

    if (
      (asset?.type === "image" && !!onUploadAsset) ||
      (asset?.type === "color" && !!onColorChange)
    ) {
      return { asset };
    }

    return null;
  };

  const findVariation = (key) => {
    if (!key) {
      return null;
    }

    for (const group of groups) {
      const variation = group.variations.find((v) => v.key === key);

      if (variation) {
        return { variation };
      }
    }

    return null;
  };

  // What a field or cell shows next to its text: its template asset, its
  // reference image(s), its variation, then its link. Each is left out when it is not
  // there to show — an asset or variation that is not in the template, or a
  // variation gated off by its `condition`.
  // Rows of a variations grid are laid out from these rather than the cells,
  // so each carries its cell's `colSpan` and `rowSpan` for `CampaignFieldRows`.
  const resolvePreviews = (cell, grid) =>
    !cell
      ? null
      : grid.type === GRID_TYPE.VARIATIONS && isTextCell(cell)
        ? { cell, colSpan: cell.colSpan, rowSpan: cell.rowSpan, isText: true }
        : {
            cell,
            colSpan: cell.colSpan,
            rowSpan: cell.rowSpan,
            referenceKey: getVariationReferenceKeys(cell).length
              ? cell.referenceKey
              : null,
            ...findAsset(cell.assetName),
            ...findVariation(cell.variationKey),
            link: getCellLink(cell),
            isDropDisabled: isCellDropDisabled(cell, grid),
          };

  // A `null` cell stays, as an empty column.
  const hasPreviews = (previews) =>
    !previews ||
    !!previews.isText ||
    !!previews.asset ||
    !!previews.referenceKey ||
    !!previews.variation ||
    !!previews.link;

  // A campaign field, as every grid shows one — a cell that is only a link
  // has no text to show.
  const renderField = (cell) => {
    const { label, value, size = "L", disabled = false } = cell;

    return !isTextCell(cell) && getCellLink(cell) ? null : (
      <CampaignField
        selectedCampaign={selectedCampaign}
        selectedPersona={selectedPersona}
        label={label}
        value={value}
        size={size}
        isDisabled={disabled || !canEditCampaign}
      />
    );
  };

  const renderPreviews = (
    { asset, referenceKey, variation, link, isDropDisabled },
    { label, description, isFluid } = {},
  ) => (
    <Fragment>
      {!!asset &&
        (renderAsset ? (
          renderAsset(asset, { label, description, isFluid })
        ) : (
          <CampaignFieldAsset
            asset={asset}
            selectedAsset={extendedAssets[asset.name]}
            // The cell's caption is the variation's (or reference's) when it
            // has one.
            label={variation || referenceKey ? undefined : label}
            description={variation || referenceKey ? undefined : description}
            isFluid={isFluid}
            scope={scope}
            colors={colors}
            onUploadAsset={onUploadAsset}
            onDeleteAsset={onDeleteAsset}
            onColorChange={onColorChange}
          />
        ))}
      {!!referenceKey && (
        <CampaignFieldReference
          referenceKey={referenceKey}
          label={variation ? undefined : label}
          description={variation ? undefined : description}
          isFluid={isFluid}
          video={video}
          templateData={templateData}
          imageVariations={imageVariations}
          isLoading={video.isLoading}
          onEditVariation={onEditVariation}
          onUploadAsset={onUploadAsset}
        />
      )}
      {!!variation &&
        (renderVariation ? (
          renderVariation(variation, { label, description, isFluid })
        ) : (
          <CampaignFieldVariation
            dependencies={structureDependencies.get(variation.key)}
            label={label}
            description={description}
            isFluid={isFluid}
            variation={variation}
            selectedCampaign={selectedCampaign}
            video={video}
            templateData={templateData}
            generatingKeys={imageVariations?.generatingKeys}
            idleKeys={imageVariations?.idleKeys}
            isLoading={video.isLoading}
            isGenerateDisabled={isGenerateDisabled}
            onEditVariation={onEditVariation}
            onGenerateVariation={onGenerateVariation}
            onUploadVariation={onUploadVariation}
            isDropDisabled={isDropDisabled}
          />
        ))}
      {!!link && (
        <CampaignFieldLink
          link={link}
          // The cell's caption is its image's when it has one.
          label={asset || referenceKey || variation ? undefined : label}
          description={
            asset || referenceKey || variation ? undefined : description
          }
          isFluid={isFluid}
        />
      )}
    </Fragment>
  );

  return (
    <div
      className={mergeStyles(
        style({
          display: "flex",
          flexDirection: "column",
          gap: 80,
          width: "full",
          maxWidth: 1200,
          marginX: "auto",
          textWrap: "pretty",
        }),
        styles,
      )}
    >
      {campaignStructure.map((group, idx) => (
        <div
          key={idx}
          className={style({
            display: "flex",
            flexDirection: "column",
            gap: 24,
            boxSizing: "border-box",
            width: "full",
          })}
        >
          <CampaignGroupHeader
            title={group.title}
            description={group.description}
          />
          {getGroupGrids(group).map((grid, gridIdx) => {
            if (grid.type === GRID_TYPE.VARIATIONS) {
              // Cells with nothing to show are left out along with the rows
              // they empty.
              const rows = grid.rows
                .map((row) =>
                  row
                    .map((cell) => resolvePreviews(cell, grid))
                    .filter(hasPreviews),
                )
                .filter((row) => row.some(Boolean))
                // A text cell sharing its row with other kinds of items lines
                // up with their previews.
                .map((row) => {
                  const hasOtherItems = row.some(
                    (cell) => !!cell && !cell.isText,
                  );

                  return row.map((cell) =>
                    cell?.isText ? { ...cell, isOffset: hasOtherItems } : cell,
                  );
                });

              if (!rows.length) {
                return null;
              }

              // The grid's variations, for regenerating them all at once.
              const gridVariations = Array.from(
                new Map(
                  rows
                    .flat()
                    .map((previews) => previews?.variation)
                    .filter(Boolean)
                    .map((variation) => [variation.key, variation]),
                ).values(),
              );
              const gridVariationKeys = gridVariations.map((v) => v.key);

              // The outdated ones, and what catches each up.
              const outdatedUpdates = gridVariations
                .map((variation) => {
                  const isPromptEdited = !!variation.data?.promptEdited;
                  const { action } = getVariationOutdatedState({
                    variation,
                    dependencies: structureDependencies.get(variation.key),
                    assets: video.data?.assets,
                    selectedCampaign,
                    video,
                    isPromptEdited,
                    isBusy:
                      !!imageVariations?.generatingKeys?.has(variation.key) ||
                      !!progressByKey?.[variation.key] ||
                      !!video.data?.assets?.find(
                        (a) => a.name === variation.key,
                      )?.isLoading,
                  });

                  return action
                    ? { key: variation.key, action, isPromptEdited }
                    : null;
                })
                .filter(Boolean);

              return (
                <div key={gridIdx}>
                  <CampaignGridHeader
                    title={grid.title}
                    description={grid.description}
                    previewLinks={getPreviewLinks(
                      grid.previewLinks,
                      templateData?.previewUrl,
                    )}
                    previewPassword={templateData?.previewPassword}
                    actions={
                      (outdatedUpdates.length > 0 || isDevMode) && (
                        <>
                          {outdatedUpdates.length > 0 &&
                            !!imageVariations?.updateVariations && (
                              <UpdateOutdatedButton
                                updates={outdatedUpdates}
                                isDisabled={video.isLoading}
                                onUpdate={imageVariations.updateVariations}
                              />
                            )}
                          {
                            // Dev mode only — see useIsDevMode.
                            isDevMode && gridVariationKeys.length > 0 && (
                              <ActionButton
                                size="XS"
                                isQuiet
                                aria-label="Regenerate all images (dev)"
                                isDisabled={isGenerateDisabled}
                                onPress={() =>
                                  regenerateVariations(gridVariationKeys)
                                }
                              >
                                <BugIcon />
                                <Text>Regenerate all</Text>
                              </ActionButton>
                            )
                          }
                        </>
                      )
                    }
                  />
                  <div
                    className={style({
                      boxSizing: "border-box",
                      marginY: 16,
                    })}
                  >
                    <CampaignFieldRows
                      rows={rows}
                      columns={VARIATIONS_GRID_COLUMNS}
                      rowGap="variations"
                      renderField={(previews) =>
                        previews.isText ? (
                          <CampaignFieldBox isOffset={previews.isOffset}>
                            {renderField(previews.cell)}
                          </CampaignFieldBox>
                        ) : (
                          <div
                            className={style({
                              display: "flex",
                              flexDirection: "column",
                              gap: 16,
                            })}
                          >
                            {renderPreviews(previews, {
                              isFluid: true,
                              label: resolveCaptionText(
                                previews.cell.label,
                                previews.variation?.label,
                              ),
                              description: previews.cell.description,
                            })}
                          </div>
                        )
                      }
                    />
                  </div>
                </div>
              );
            }

            // One cell per linked image — asset, reference(s), variation — or
            // link, field by field.
            const linked = grid.rows
              .flat()
              .map((cell) => resolvePreviews(cell, grid))
              .filter(Boolean)
              .filter(hasPreviews)
              .flatMap(
                ({
                  cell,
                  asset,
                  referenceKey,
                  variation,
                  link,
                  isDropDisabled,
                }) => [
                  ...(asset ? [{ cell, asset }] : []),
                  ...(referenceKey ? [{ cell, referenceKey }] : []),
                  ...(variation ? [{ cell, variation, isDropDisabled }] : []),
                  ...(link ? [{ cell, link }] : []),
                ],
              );

            const linkedColSpan = Math.min(
              Math.max(
                Number.isInteger(grid.linkedColSpan) ? grid.linkedColSpan : 2,
                1,
              ),
              VARIATIONS_GRID_COLUMNS - 1,
            );
            const fieldsColSpan = VARIATIONS_GRID_COLUMNS - linkedColSpan;

            // The images beside the fields, `linkedColSpan` to a line — a lone
            // one filling the line. The fields span all of the lines.
            const imageLines = [];

            for (let idx = 0; idx < linked.length; idx += linkedColSpan) {
              imageLines.push(
                linked.slice(idx, idx + linkedColSpan).map((previews) => ({
                  previews,
                  colSpan: linked.length === 1 ? linkedColSpan : 1,
                })),
              );
            }

            const fieldsCell = {
              isFields: true,
              isSubgrid: true,
              colSpan: fieldsColSpan,
              rowSpan: imageLines.length,
            };

            return (
              <div
                key={gridIdx}
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                })}
              >
                <CampaignGridHeader
                  title={grid.title}
                  description={grid.description}
                  previewLinks={getPreviewLinks(
                    grid.previewLinks,
                    templateData?.previewUrl,
                  )}
                  previewPassword={templateData?.previewPassword}
                />
                {linked.length > 0 ? (
                  <CampaignFieldRows
                    rows={imageLines.map((line, lineIdx) =>
                      lineIdx === 0 ? [fieldsCell, ...line] : line,
                    )}
                    columns={VARIATIONS_GRID_COLUMNS}
                    rowGap="variations"
                    renderField={(cell) =>
                      cell.isFields ? (
                        <CampaignFieldBox isSubgrid isOffset>
                          <CampaignFieldRows
                            rows={grid.rows}
                            columns={fieldsColSpan}
                            subgrid
                            renderField={renderField}
                          />
                        </CampaignFieldBox>
                      ) : (
                        renderPreviews(cell.previews, { isFluid: true })
                      )
                    }
                  />
                ) : (
                  <CampaignFieldBox>
                    <CampaignFieldRows
                      rows={grid.rows}
                      renderField={renderField}
                    />
                  </CampaignFieldBox>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

CampaignVisualsPreview.propTypes = {
  renderVariation: PropTypes.func,
  renderAsset: PropTypes.func,
  campaignStructure: PropTypes.array,
  selectedCampaign: PropTypes.object,
  selectedPersona: PropTypes.object,
  canEditCampaign: PropTypes.bool,
  video: PropTypes.object.isRequired,
  templateData: PropTypes.object,
  imageVariations: PropTypes.object,
  colors: PropTypes.array,
  isGenerateDisabled: PropTypes.bool,
  onEditVariation: PropTypes.func,
  onGenerateVariation: PropTypes.func,
  onUploadVariation: PropTypes.func,
  onUploadAsset: PropTypes.func,
  onDeleteAsset: PropTypes.func,
  onColorChange: PropTypes.func,
  styles: PropTypes.string,
};
